import { Button } from "@my-saas/ui";
import {
  ArrowLeftIcon,
  BellRingingIcon,
  ChatsCircleIcon,
  GlobeHemisphereWestIcon,
  LightningIcon,
  MapTrifoldIcon,
  ShieldCheckIcon,
  SpeakerHighIcon,
  UsersThreeIcon,
} from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router";

import { Logo } from "@/client/components/logo";

/**
 * cig.chat OREF Alert Platform — Hebrew RTL landing page.
 *
 * Audience: ישובים / מושבים / קיבוצים / עיריות that need to fan-out OREF
 * (Pikud Ha'oref) alerts into per-region WhatsApp groups, multi-language,
 * with no engineering team to operate.
 */
export const HomePage = () => {
  return (
    <main className="relative isolate bg-background text-foreground" dir="rtl">
      <Helmet prioritizeSeoTags>
        <html lang="he-IL" dir="rtl" />
        <title>cig.chat — התרעות פיקוד העורף לקבוצות וואטסאפ של הישוב שלכם</title>
        <meta
          name="description"
          content="הפצה אוטומטית של התרעות פיקוד העורף לקבוצות WhatsApp ייעודיות עבור ישובים, מושבים, קיבוצים ועיריות. הגדרת אזורים, שיוך קבוצות לפי שפה, בלי כתיבת קוד."
        />
        <meta property="og:title" content="cig.chat — התרעות פיקוד העורף לקבוצות WhatsApp" />
        <meta property="og:locale" content="he_IL" />
      </Helmet>

      {/* ── Hero ────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden pt-32 pb-20 sm:pt-40 sm:pb-28">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80"
        >
          <div
            className="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-red-600 via-amber-500 to-blue-600 opacity-30 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]"
            style={{
              clipPath:
                "polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)",
            }}
          />
        </div>

        <div className="container max-w-5xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mx-auto mb-8 flex justify-center"
          >
            <Logo size={88} />
          </motion.div>

          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.4 }}
            className="inline-flex items-center gap-2 rounded-full border bg-muted/40 px-3 py-1 text-xs font-medium"
          >
            <BellRingingIcon weight="fill" className="size-4 text-red-500" />
            התרעות פיקוד העורף · בזמן אמת · ישירות לקבוצות WhatsApp
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.5 }}
            className="mt-6 text-balance text-4xl font-bold tracking-tight sm:text-6xl"
          >
            כל התושבים שלכם מקבלים <span className="text-primary">התרעה אחת</span>,
            <br className="hidden sm:block" /> במהירות, בשפה הנכונה.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="mx-auto mt-6 max-w-2xl text-balance text-lg leading-relaxed text-muted-foreground"
          >
            מערכת אוטומטית להפצת התרעות פיקוד העורף לקבוצות WhatsApp ייעודיות עבור{" "}
            <strong className="text-foreground">ישובים, מושבים, קיבוצים ועיריות</strong>.
            הגדירו אזורים, שייכו קבוצות לפי שפה, ותנו למערכת לעשות את השאר.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.5 }}
            className="mt-10 flex flex-wrap items-center justify-center gap-3"
          >
            <Link to="/auth/register">
              <Button size="lg" variant="primary" className="gap-2">
                התחלת שימוש חינם
                <ArrowLeftIcon weight="bold" className="size-4" />
              </Button>
            </Link>
            <Link to="/#how-it-works">
              <Button size="lg" variant="outline">
                איך זה עובד?
              </Button>
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="mt-8 text-xs text-muted-foreground"
          >
            ללא כרטיס אשראי · ללא התקנה · עברית + אנגלית מלאות · חיבור ישיר לפיקוד העורף
          </motion.div>
        </div>
      </section>

      {/* ── Pain point ──────────────────────────────────────────────────── */}
      <section className="bg-muted/30 py-16 sm:py-20">
        <div className="container max-w-4xl">
          <h2 className="text-balance text-center text-2xl font-bold tracking-tight sm:text-3xl">
            למה זה כל-כך מסובך היום
          </h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-3">
            <div className="rounded-lg border bg-background p-5">
              <div className="text-3xl">⏱️</div>
              <h3 className="mt-2 font-semibold">כל שנייה קריטית</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                התרעות שמגיעות באיחור או לא מגיעות בכלל מסכנות חיים. אנשים מסתכלים על WhatsApp לפני שהם בודקים את אפליקציית פיקוד העורף.
              </p>
            </div>
            <div className="rounded-lg border bg-background p-5">
              <div className="text-3xl">🌐</div>
              <h3 className="mt-2 font-semibold">קהילות רב-לשוניות</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                ישוב אחד יכול להחזיק 4 קבוצות — תושבים, עובדים זרים, מתנדבים, משפחות. כל קבוצה צריכה את ההתרעה בשפה אחרת.
              </p>
            </div>
            <div className="rounded-lg border bg-background p-5">
              <div className="text-3xl">🗺️</div>
              <h3 className="mt-2 font-semibold">חלוקה גיאוגרפית מורכבת</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                עירייה גדולה חולשת על שכונות שונות. צפון העיר לא צריך לקבל התרעה רק כי הדרום בסכנה.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── How it works ────────────────────────────────────────────────── */}
      <section id="how-it-works" className="py-20 sm:py-28">
        <div className="container max-w-5xl">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-balance text-3xl font-bold tracking-tight sm:text-4xl">
              איך זה עובד — ב-4 צעדים
            </h2>
            <p className="mt-4 text-muted-foreground">
              הגדרה חד-פעמית של 10 דקות. אחרי זה — המערכת רצה לבד 24/7.
            </p>
          </div>

          <div className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            <Step
              n={1}
              icon={<MapTrifoldIcon weight="duotone" className="size-7" />}
              title="הגדירו אזורים"
              body="צרו קבוצות של ערים לפי שכונות, איזורים גיאוגרפיים או מטה הישוב. למשל: 'צפון', 'דרום', 'גלעד'."
            />
            <Step
              n={2}
              icon={<ChatsCircleIcon weight="duotone" className="size-7" />}
              title="חברו את WhatsApp"
              body="חיבור פשוט דרך Green API. סינכרון אוטומטי של כל הקבוצות הקיימות."
            />
            <Step
              n={3}
              icon={<GlobeHemisphereWestIcon weight="duotone" className="size-7" />}
              title="שייכו קבוצות לאזורים"
              body="קבוצה X = אזור צפון בעברית · קבוצה Y = אזור צפון באנגלית · קבוצה Z = כל הישוב. שליטה מלאה."
            />
            <Step
              n={4}
              icon={<LightningIcon weight="duotone" className="size-7" />}
              title="קבלו התרעות בזמן אמת"
              body="המערכת מאזינה לפיקוד העורף 24/7 ושולחת התרעה רק לקבוצות שרלוונטיות לאזור שנפגע."
            />
          </div>
        </div>
      </section>

      {/* ── Audience / features ─────────────────────────────────────────── */}
      <section id="features" className="bg-muted/30 py-20 sm:py-28">
        <div className="container max-w-5xl">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-balance text-3xl font-bold tracking-tight sm:text-4xl">
              מי משתמש ב-cig.chat
            </h2>
            <p className="mt-4 text-muted-foreground">
              המערכת נבנתה מהשטח, יחד עם רכזי בטחון של ישובים אמיתיים בעוטף ובצפון.
            </p>
          </div>

          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <AudienceCard emoji="🏡" title="ישובים" body="מועצות מקומיות, ישובים קהילתיים, חוות. ניהול עצמאי של רכז הביטחון או המזכירות." />
            <AudienceCard emoji="🌾" title="מושבים" body="חלוקה לפי שדרות, רחובות או משפחות. שילוב קבוצות עברית + תאית/רוסית/אנגלית." />
            <AudienceCard emoji="🤝" title="קיבוצים" body="חברי קיבוץ, ענפים, אורחים — כל קבוצה מקבלת את ההתרעה הרלוונטית." />
            <AudienceCard emoji="🏙️" title="עיריות" body="חלוקה לפי שכונות. שליטה ברמת רחוב. מצב חירום: שליחה לכל העיר." />
          </div>

          <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <Feature
              icon={<BellRingingIcon weight="duotone" className="size-6" />}
              title="3 מקורות התרעה במקביל"
              body="HTTP API של פיקוד העורף + MQTT דרך ioref + Tzevaadom WebSocket. דדופליקציה אוטומטית — תושב אחד מקבל הודעה אחת."
            />
            <Feature
              icon={<MapTrifoldIcon weight="duotone" className="size-6" />}
              title="אזורים מותאמים אישית"
              body="לא רק שמות-ערים של פיקוד העורף. הגדירו אזורים פנימיים — שכונות, אגפים, רחובות — בכל רזולוציה."
            />
            <Feature
              icon={<UsersThreeIcon weight="duotone" className="size-6" />}
              title="קבוצות לפי שפה"
              body="עברית · אנגלית · ערבית · רוסית · תאית. כל אזור יכול להחזיק כמה קבוצות בשפות שונות בו-זמנית."
            />
            <Feature
              icon={<SpeakerHighIcon weight="duotone" className="size-6" />}
              title="הודעות מותאמות"
              body="כל סוג התרעה (ירי רקטות / חדירת כלי טיס / סיום אירוע) מקבל ניסוח, באנר ופורמט נפרדים — בעברית ובאנגלית."
            />
            <Feature
              icon={<ShieldCheckIcon weight="duotone" className="size-6" />}
              title="מצב 'בדיקה'"
              body="לפני יציאה ל-production, כל ההתרעות נשלחות לקבוצת בדיקה אחת בלבד. אפס סיכון של דליפת הודעות לפני שתהיו מוכנים."
            />
            <Feature
              icon={<LightningIcon weight="duotone" className="size-6" />}
              title="חיווי בזמן אמת"
              body="לוח-מחוונים עם feed חי של כל ההתרעות שהמערכת מעבדת, כולל אילו נשלחו ואילו נחסמו ב-dedup."
            />
          </div>
        </div>
      </section>

      {/* ── Pricing / Stage-1 free ──────────────────────────────────────── */}
      <section id="pricing" className="py-20 sm:py-28">
        <div className="container max-w-3xl text-center">
          <h2 className="text-balance text-3xl font-bold tracking-tight sm:text-4xl">
            שלב השקה — הכל חינם
          </h2>
          <p className="mt-4 text-balance text-lg text-muted-foreground">
            לא צריך כרטיס אשראי. לא צריך תקופת ניסיון. הירשמו, הגדירו את הישוב שלכם, ותתחילו לקבל התרעות בקבוצות WhatsApp עוד היום.
          </p>

          <div className="mx-auto mt-10 max-w-md rounded-xl border bg-card p-8 shadow-sm">
            <div className="text-center">
              <div className="text-sm font-medium text-muted-foreground">
                לכל ישוב / מושב / עירייה
              </div>
              <div className="mt-4 text-5xl font-bold">
                ₪0
                <span className="text-base font-normal text-muted-foreground"> / חודש</span>
              </div>
              <div className="mt-2 text-xs text-muted-foreground">בשלב ההשקה</div>
            </div>

            <ul className="mt-8 space-y-3 text-sm">
              <Check>אזורים בלתי מוגבלים</Check>
              <Check>קבוצות WhatsApp בלתי מוגבלות</Check>
              <Check>כל המקורות (HTTP, MQTT, Tzevaadom)</Check>
              <Check>עברית + אנגלית + תוספת שפות</Check>
              <Check>חיווי חי + יומן הודעות</Check>
              <Check>תמיכה במייל</Check>
            </ul>

            <Link to="/auth/register" className="mt-8 block">
              <Button size="lg" variant="primary" className="w-full gap-2">
                התחלת שימוש
                <ArrowLeftIcon weight="bold" className="size-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ── Closing CTA ─────────────────────────────────────────────────── */}
      <section className="border-t bg-muted/30 py-20">
        <div className="container max-w-3xl text-center">
          <h2 className="text-balance text-3xl font-bold tracking-tight">
            כל דקה שעוברת — תושבים בסכנה.
          </h2>
          <p className="mt-3 text-balance text-muted-foreground">
            הגדרה ראשונית: 10 דקות. השפעה: על כל התושבים בישוב שלכם.
          </p>
          <Link to="/auth/register" className="mt-8 inline-block">
            <Button size="lg" variant="primary" className="gap-2">
              הירשמו עכשיו — בחינם
              <ArrowLeftIcon weight="bold" className="size-4" />
            </Button>
          </Link>
        </div>
      </section>
    </main>
  );
};

// ── small building blocks ─────────────────────────────────────────────

function Step({ n, icon, title, body }: { n: number; icon: React.ReactNode; title: string; body: string }) {
  return (
    <div className="relative rounded-lg border bg-card p-6">
      <div className="absolute -top-3 right-4 inline-flex size-7 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
        {n}
      </div>
      <div className="text-primary">{icon}</div>
      <h3 className="mt-3 font-semibold">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>
    </div>
  );
}

function AudienceCard({ emoji, title, body }: { emoji: string; title: string; body: string }) {
  return (
    <div className="rounded-lg border bg-card p-5">
      <div className="text-3xl">{emoji}</div>
      <h3 className="mt-2 font-semibold">{title}</h3>
      <p className="mt-2 text-sm text-muted-foreground">{body}</p>
    </div>
  );
}

function Feature({ icon, title, body }: { icon: React.ReactNode; title: string; body: string }) {
  return (
    <div className="rounded-lg border bg-card p-6">
      <div className="text-primary">{icon}</div>
      <h3 className="mt-3 font-semibold">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>
    </div>
  );
}

function Check({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-center gap-2 text-start">
      <span className="inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-600">
        ✓
      </span>
      <span>{children}</span>
    </li>
  );
}
